import React from 'react';
import {
  ArrowClockwise,
  ClipboardText,
  Lock,
  WarningCircle,
  XCircle,
} from '@phosphor-icons/react';
import { Button } from '@/components/ui-kit/button';
import { Skeleton } from '@/components/ui-kit/skeleton';
import type { AuditLogEntry } from '../types/audit-log.types';
import { AuditLogRow } from './audit-log-row';

interface AuditLogTableProps {
  entries: AuditLogEntry[];
  isLoading: boolean;
  isError: boolean;
  isForbidden: boolean;
  errorMessage?: string;
  hasFilters: boolean;
  onClearFilters: () => void;
  onRetry: () => void;
  /** Rendered in the table footer (pagination component). */
  footer?: React.ReactNode;
}

export const AuditLogTable: React.FC<AuditLogTableProps> = ({
  entries,
  isLoading,
  isError,
  isForbidden,
  errorMessage,
  hasFilters,
  onClearFilters,
  onRetry,
  footer,
}) => {
  if (isForbidden) {
    return (
      <div className="bg-white border border-neutral-200 rounded-lg shadow-e1 p-12 text-center flex flex-col items-center justify-center gap-3">
        <div className="size-12 rounded-full bg-neutral-100 flex items-center justify-center text-neutral-600 mb-1">
          <Lock className="size-6 text-neutral-600" />
        </div>
        <h2 className="text-lg font-semibold text-neutral-900">
          You don&apos;t have access to the audit log
        </h2>
        <p className="text-sm text-neutral-600 max-w-md">
          Only owners and administrators are permitted to view audit records. Contact your workspace administrator for access.
        </p>
      </div>
    );
  }

  const renderSkeletonRows = () => (
    <>
      {Array.from({ length: 6 }).map((_, idx) => (
        <tr key={idx} className="border-b border-[#F4F5F6]">
          <td className="px-4 py-3">
            <Skeleton className="h-4 w-36" />
          </td>
          <td className="px-4 py-3">
            <Skeleton className="h-4 w-28" />
          </td>
          <td className="px-4 py-3">
            <Skeleton className="h-4 w-32" />
          </td>
          <td className="px-4 py-3">
            <Skeleton className="h-4 w-64" />
          </td>
          <td className="px-4 py-3">
            <Skeleton className="h-4 w-32" />
          </td>
        </tr>
      ))}
    </>
  );

  const renderStateRow = () => {
    if (isError) {
      return (
        <tr>
          <td colSpan={5} className="px-6 py-14 align-middle">
            <div className="flex flex-col items-center text-center gap-3 max-w-[380px] mx-auto">
              <WarningCircle className="size-8 text-destructive" />
              <div className="text-h4 font-semibold text-neutral-900">
                Couldn&apos;t load the audit log
              </div>
              <p className="text-neutral-600 text-sm">
                {errorMessage ||
                  'Something went wrong while fetching entries. Check your connection and try again.'}
              </p>
              <div className="flex items-center gap-2.5 mt-2">
                <Button
                  id="retryBtn"
                  data-testid="audit-retry-btn"
                  variant="default"
                  onClick={onRetry}
                  className="gap-1.5"
                >
                  <ArrowClockwise className="size-4" />
                  Retry
                </Button>
              </div>
            </div>
          </td>
        </tr>
      );
    }

    if (entries.length === 0) {
      if (hasFilters) {
        return (
          <tr>
            <td colSpan={5} className="px-6 py-14 align-middle">
              <div className="flex flex-col items-center text-center gap-3 max-w-[360px] mx-auto">
                <ClipboardText className="size-8 text-[#707070]" />
                <div className="text-h4 font-semibold text-neutral-900">
                  No entries match these filters
                </div>
                <p className="text-neutral-600 text-sm">
                  Nothing matches these filters yet. Try widening the date range or clearing a filter.
                </p>
                <div className="flex items-center gap-2.5 mt-2">
                  <Button
                    id="emptyClearBtn"
                    data-testid="empty-clear-btn"
                    variant="default"
                    onClick={onClearFilters}
                    className="gap-1.5"
                  >
                    <XCircle className="size-4" />
                    Clear filters
                  </Button>
                </div>
              </div>
            </td>
          </tr>
        );
      }

      return (
        <tr>
          <td colSpan={5} className="px-6 py-14 align-middle">
            <div className="flex flex-col items-center text-center gap-3 max-w-[360px] mx-auto">
              <ClipboardText className="size-8 text-[#707070]" />
              <div className="text-h4 font-semibold text-neutral-900">
                No activity recorded yet
              </div>
              <p className="text-neutral-600 text-sm">
                Activity will appear here as team members take actions in this workspace.
              </p>
            </div>
          </td>
        </tr>
      );
    }

    return null;
  };

  return (
    <div className="bg-white border border-neutral-200 rounded-lg shadow-e1 overflow-hidden flex flex-col flex-1 min-h-0">
      <div id="tableScroll" className="overflow-auto flex-1 min-h-0">
        <table id="dataTable" className="w-full border-collapse min-w-[900px]">
          <thead>
            <tr className="bg-neutral-50 border-b border-neutral-200">
              <th
                scope="col"
                className="sticky top-0 z-10 bg-neutral-50 px-4 py-3 text-left text-caption font-semibold text-neutral-600 whitespace-nowrap"
              >
                Actor
              </th>
              <th
                scope="col"
                className="sticky top-0 z-10 bg-neutral-50 px-4 py-3 text-left text-caption font-semibold text-neutral-600 whitespace-nowrap"
              >
                Action
              </th>
              <th
                scope="col"
                className="sticky top-0 z-10 bg-neutral-50 px-4 py-3 text-left text-caption font-semibold text-neutral-600 whitespace-nowrap"
              >
                Resource
              </th>
              <th
                scope="col"
                className="sticky top-0 z-10 bg-neutral-50 px-4 py-3 text-left text-caption font-semibold text-neutral-600 whitespace-nowrap"
              >
                Summary
              </th>
              <th
                scope="col"
                className="sticky top-0 z-10 bg-neutral-50 px-4 py-3 text-left text-caption font-semibold text-neutral-600 whitespace-nowrap"
              >
                Time
              </th>
            </tr>
          </thead>
          <tbody id="rows">
            {isLoading && entries.length === 0 ? (
              renderSkeletonRows()
            ) : entries.length === 0 ? (
              renderStateRow()
            ) : (
              entries.map((entry) => <AuditLogRow key={entry.itemId} entry={entry} />)
            )}
          </tbody>
        </table>
      </div>

      {footer}
    </div>
  );
};
